// src/App.js
import React, { useState } from "react";
import axios from "axios";
import Dashboard from "./Dashboard";

const API_URL = "/api";

// Very light heuristic to guess the pasted scan type
function detectScanType(text = "") {
  const t = text.trim();
  if (!t) return "unknown";

  // netsh (Windows): often contains "BSSID", "Signal", "Radio type", "SSID <name>" blocks
  const looksLikeNetsh =
    /BSSID\s*\d+|Signal\s*:\s*-\d+%|Radio\s*type|SSID\s*\d+\s*:/i.test(t) ||
    /^Interface name/i.test(t);

  // wl / airport (macOS): lines like "bssid=xx:xx..., rssi=-NN"
  const looksLikeWifiScan = /bssid=([0-9a-f]{2}:){5}[0-9a-f]{2}/i.test(t) && /rssi=-?\d+/.test(t);

  if (looksLikeWifiScan && !looksLikeNetsh) return "wifiscan";
  if (looksLikeNetsh && !looksLikeWifiScan) return "netsh";

  // If both regexes match (rare), prefer the one with more hits
  const netshHits = (t.match(/BSSID\s*\d+|Signal\s*:\s*-\d+%|Radio\s*type|SSID\s*\d+\s*:/gi) || []).length;
  const wifiHits = (t.match(/bssid=([0-9a-f]{2}:){5}[0-9a-f]{2}|rssi=-?\d+/gi) || []).length;

  if (wifiHits > netshHits) return "wifiscan";
  if (netshHits > wifiHits) return "netsh";
  return "unknown";
}

function parseApiError(err) {
  // Best-effort message extraction to help debugging in the UI/console
  const res = err?.response;
  if (!res) return "Network error or no response from server";
  const data = res.data;
  if (typeof data === "string") return `${res.status} ${res.statusText}: ${data}`;
  if (data?.detail) {
    if (Array.isArray(data.detail)) {
      return `${res.status} ${res.statusText}: ${data.detail.map(d => d.msg || JSON.stringify(d)).join("; ")}`;
    }
    return `${res.status} ${res.statusText}: ${data.detail}`;
  }
  return `${res.status} ${res.statusText}`;
}

const App = () => {
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [apiKey, setApiKey] = useState(localStorage.getItem("api_key") || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API_URL}/login`, { username, password });
      localStorage.setItem("token", res.data.access_token);
      localStorage.setItem("api_key", res.data.api_key);
      setToken(res.data.access_token);
      setApiKey(res.data.api_key);
      setError("");
    } catch (err) {
      console.error("Login failed", err);
      setError("Login failed. Please check your credentials.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("api_key");
    setToken("");
    setApiKey("");
    setUsername("");
    setPassword("");
  };

  // Existing: submit Windows netsh output
  const onSubmitNetsh = async (netshOutput) => {
    try {
      const res = await axios.post(
        `${API_URL}/query/netsh`,
        { netsh_output: netshOutput },
        { headers: { "x-api-key": apiKey } }
      );
      return res.data;
    } catch (err) {
      console.error("Error submitting netsh output:", parseApiError(err), err);
      return { error: parseApiError(err) };
    }
  };

  // New: submit macOS wl/airport scan output
  const onSubmitWifiscan = async (rawOutput) => {
    try {
      const res = await axios.post(
        `${API_URL}/query/wifiscan`,
        { raw_output: rawOutput },
        { headers: { "x-api-key": apiKey } }
      );
      return res.data;
    } catch (err) {
      console.error("Error submitting wifiscan output:", parseApiError(err), err);
      return { error: parseApiError(err) };
    }
  };

  // New: auto-detect and route to the right endpoint
  const onSubmitAuto = async (pastedText) => {
    const kind = detectScanType(pastedText);
    if (kind === "netsh") {
      return onSubmitNetsh(pastedText);
    } else if (kind === "wifiscan") {
      return onSubmitWifiscan(pastedText);
    } else {
      const msg =
        "Couldn’t auto-detect scan type. Paste Windows “netsh wlan show networks mode=bssid” or macOS “airport -s / wl scan” output.";
      console.warn(msg);
      return { error: msg };
    }
  };

  const onFetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/history`, {
        headers: { "x-api-key": apiKey },
      });
      return res.data;
    } catch (err) {
      console.error("Error fetching history:", parseApiError(err), err);
      return { error: parseApiError(err) };
    }
  };

  const onSelectSSID = (entry) => {
    console.log("Selected SSID/BSSID:", entry);
    // Map snapping handled inside Map component via prop "selected"
  };

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", padding: "2rem" }}>
      {!token ? (
        <div style={{ maxWidth: 360 }}>
          <h2>Login to FindIt</h2>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={{ padding: "0.5rem", margin: "0.5rem 0", width: "100%" }}
            autoComplete="username"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ padding: "0.5rem", margin: "0.5rem 0", width: "100%" }}
            autoComplete="current-password"
          />
          <button onClick={handleLogin} style={{ padding: "0.6rem 1rem", marginTop: "0.5rem" }}>
            Login
          </button>
          {error && <p style={{ color: "crimson" }}>{error}</p>}
        </div>
      ) : (
        <Dashboard
          apiKey={apiKey}
          onLogout={handleLogout}
          onSubmitNetsh={onSubmitNetsh}
          onSubmitWifiscan={onSubmitWifiscan}   
          onSubmitAuto={onSubmitAuto}           
          onFetchHistory={onFetchHistory}
          onSelectSSID={onSelectSSID}
        />
      )}
    </div>
  );
};

export default App;

